import type { Ref } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { NewsData } from "~/layers/media/types/News";
import type { SalesData } from "~/layers/media/types/Sales";
import type { MediaEntry } from "~/layers/media/types/Media";

/** Вкладки раздела медиа: «Все» /media, «Акции» /sales, «Новости» /news. */
export type MediaSection = "media" | "sales" | "news";

/** Сколько записей на странице. ТЗ IT-797, п. 13: 10–12 штук. */
export const MEDIA_PER_PAGE = 12;

export interface MediaSectionConfig {
  /** Адрес первой страницы, он же адрес вкладки. */
  path: string;
  /** Имя маршрута страниц пагинации: по нему макет подсвечивает вкладку. */
  pageRouteName: string;
  /** Заголовок раздела; на страницах пагинации к нему добавляется номер. */
  heading: string;
  seoTitle: string;
  seoDescription: string;
  /** Формы слова для подписи «Всего N …»: одна, две-четыре, пять и больше. */
  countForms: [string, string, string];
}

/**
 * Постраничный вывод у всех трёх вкладок одинаковый (IT-797 п. 13 для новостей,
 * IT-1253 для «Все» и «Акций»): первая страница на адресе вкладки, дальше
 * /news/page2, /sales/page2, /media/page2. Title и description у вкладок разные,
 * иначе страницы пагинации трёх вкладок дублировали бы друг друга.
 */
export const MEDIA_SECTIONS: Record<MediaSection, MediaSectionConfig> = {
  media: {
    path: "/media",
    pageRouteName: "media-page",
    heading: "Акции и новости",
    seoTitle: "Акции и новости от застройщика",
    seoDescription:
      "Акции и новости от застройщика Атлас Девелопмент. Старты продаж, скидки на квартиры, ипотечные программы, ход строительства",
    countForms: ["материал", "материала", "материалов"],
  },
  sales: {
    path: "/sales",
    pageRouteName: "sales-page",
    heading: "Акции",
    seoTitle: "Акции и спецпредложения от застройщика",
    seoDescription:
      "Актуальные акции и скидки на квартиры в новостройках Атлас Девелопмент. Ипотечные программы, рассрочка, специальные условия покупки",
    countForms: ["акция", "акции", "акций"],
  },
  news: {
    path: "/news",
    pageRouteName: "news-page",
    heading: "Новости",
    seoTitle: "Новости от застройщика",
    seoDescription: "Официальные новости от застройщика. Старты продаж новых проектов. Корпоративные новости",
    countForms: ["статья", "статьи", "статей"],
  },
};

/** Адрес страницы N раздела: первая без номера. */
export const mediaPagePath = (section: MediaSection, page: number): string =>
  page <= 1 ? MEDIA_SECTIONS[section].path : `${MEDIA_SECTIONS[section].path}/page${page}`;

const byDateDesc = (a: MediaEntry, b: MediaEntry) =>
  new Date(b.attributes?.publication_date).getTime() - new Date(a.attributes?.publication_date).getTime();

/**
 * Нарезка ленты вкладки на страницы.
 *
 * Данные не запрашиваем: весь список уже загрузил и раздал через provide макет
 * `media` — он же считает счётчики вкладок, и второй запрос за тем же самым был бы
 * лишним. Query (фильтры по году и проекту) переносим между страницами.
 */
export function useMediaPagination(section: MediaSection, page: Ref<number> | (() => number)) {
  const route = useRoute();
  const newsData = inject<Ref<NewsData[]>>("news");
  const salesData = inject<Ref<SalesData[]>>("sales");
  const pending = inject<Ref<boolean>>("media_pending");
  const config = MEDIA_SECTIONS[section];

  const all = computed<MediaEntry[]>(() => {
    const news = [...(newsData?.value ?? [])].sort(byDateDesc);
    if (section === "news") return news;
    const sales = [...(salesData?.value ?? [])].sort(byDateDesc);
    if (section === "sales") return sales;
    // «Все»: сначала все акции, потом все новости, внутри по дате
    return [...sales, ...news];
  });

  const total = computed(() => all.value.length);
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / MEDIA_PER_PAGE)));

  const currentPage = computed(() => {
    const raw = typeof page === "function" ? page() : page.value;
    const n = Math.floor(Number(raw));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  });

  const items = computed<MediaEntry[]>(() => {
    const start = (currentPage.value - 1) * MEDIA_PER_PAGE;
    return all.value.slice(start, start + MEDIA_PER_PAGE);
  });

  /** Адрес страницы N с сохранением текущих фильтров. */
  const linkForPage = (n: number): RouteLocationRaw => ({
    path: mediaPagePath(section, n),
    query: { ...route.query },
  });

  return { config, all, items, total, pageCount, currentPage, pending, linkForPage };
}

/** «3 статьи», «6 акций», «37 материалов» — для подписи под списком. */
export function mediaCountLabel(section: MediaSection, count: number): string {
  const [one, few, many] = MEDIA_SECTIONS[section].countForms;
  const mod100 = count % 100;
  const mod10 = count % 10;
  let word = many;
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) word = one;
    else if (mod10 >= 2 && mod10 <= 4) word = few;
  }
  return `${count} ${word}`;
}
